import { Constants } from '@pwc/pwc-deals-web-common';

interface UserMatrixInterface {
  isDeleteEnabled?: boolean;
  isChangeEnabled?: boolean;
  isCreateEnabled?: boolean;
}

interface UserMatrixByRolesInterface {
  [role: string]: UserMatrixInterface;
}

interface MatrixInterface {
  [key: string]: UserMatrixByRolesInterface;
}

const WORKBENCH = 'WorkBench';
const JUNCTION = 'Junction';
const DTP = 'DTP';

export const isDtp = (projectSource: string): boolean =>
  (projectSource || '').toLowerCase() === DTP.toLowerCase();
export const isWorkbenchDigitalStart = (projectSource: string): boolean =>
  (projectSource || '').toLowerCase() === WORKBENCH.toLowerCase();
export const isJunction = (projectSource: string): boolean =>
  (projectSource || JUNCTION).toLowerCase() === JUNCTION.toLowerCase();

const READ_ONLY = {
  isDeleteEnabled: false,
  isChangeEnabled: false,
  isCreateEnabled: false,
};

const ALL_ACTIONS = {
  isDeleteEnabled: true,
  isChangeEnabled: true,
  isCreateEnabled: true,
};

const UserSettingsRightsMatrix: MatrixInterface = {
  [WORKBENCH]: {
    [Constants.USER_ROLES.ENGAGEMENT_LEAD]: READ_ONLY,
    [Constants.USER_ROLES.ENGAGEMENT_MANAGER]: READ_ONLY,
    [Constants.USER_ROLES.PROJECT_ADMIN]: READ_ONLY,
    [Constants.USER_ROLES.APPROVER]: {
      isDeleteEnabled: false,
      isChangeEnabled: true,
      isCreateEnabled: false,
    },
    [Constants.USER_ROLES.TEAM_MEMBER]: {
      isDeleteEnabled: false,
      isChangeEnabled: true,
      isCreateEnabled: false,
    },
    [Constants.USER_ROLES.PWC_READONLY]: {
      isDeleteEnabled: false,
      isChangeEnabled: true,
      isCreateEnabled: false,
    },
    [Constants.USER_ROLES.PWC_READONLY_DISPLAY]: {
      isDeleteEnabled: false,
      isChangeEnabled: true,
      isCreateEnabled: false,
    },
    [Constants.USER_ROLES.CLIENT_ADMIN]: ALL_ACTIONS,
    [Constants.USER_ROLES.CLIENT_TEAM_MEMBER]: ALL_ACTIONS,
  },
  [JUNCTION]: {
    [Constants.USER_ROLES.ENGAGEMENT_LEAD]: {
      isDeleteEnabled: true,
      isChangeEnabled: false,
      isCreateEnabled: true,
    },
    [Constants.USER_ROLES.ENGAGEMENT_MANAGER]: {
      isDeleteEnabled: true,
      isChangeEnabled: false,
      isCreateEnabled: true,
    },
    [Constants.USER_ROLES.PROJECT_ADMIN]: ALL_ACTIONS,
    [Constants.USER_ROLES.APPROVER]: ALL_ACTIONS,
    [Constants.USER_ROLES.TEAM_MEMBER]: ALL_ACTIONS,
    [Constants.USER_ROLES.PWC_READONLY]: ALL_ACTIONS,
    [Constants.USER_ROLES.PWC_READONLY_DISPLAY]: ALL_ACTIONS,
    [Constants.USER_ROLES.CLIENT_ADMIN]: ALL_ACTIONS,
    [Constants.USER_ROLES.CLIENT_TEAM_MEMBER]: ALL_ACTIONS,
  },
  [DTP]: {
    [Constants.USER_ROLES.ENGAGEMENT_LEAD]: READ_ONLY,
    [Constants.USER_ROLES.ENGAGEMENT_MANAGER]: READ_ONLY,
    [Constants.USER_ROLES.PROJECT_ADMIN]: READ_ONLY,
    [Constants.USER_ROLES.APPROVER]: READ_ONLY,
    [Constants.USER_ROLES.TEAM_MEMBER]: READ_ONLY,
    [Constants.USER_ROLES.PWC_READONLY]: READ_ONLY,
    [Constants.USER_ROLES.PWC_READONLY_DISPLAY]: READ_ONLY,
    [Constants.USER_ROLES.CLIENT_ADMIN]: READ_ONLY,
    [Constants.USER_ROLES.CLIENT_TEAM_MEMBER]: READ_ONLY,
  },
};

const getProjectSource = (
  projectSource: string
): UserMatrixByRolesInterface | null => {
  let key;
  if (isDtp(projectSource)) {
    key = DTP;
  } else if (isWorkbenchDigitalStart(projectSource)) {
    key = WORKBENCH;
  } else if (isJunction(projectSource)) {
    key = JUNCTION;
  } else {
    return null;
  }

  return UserSettingsRightsMatrix[key];
};

export const getEnabledMatrix = (
  projectSource: string,
  role: string,
  withRoleChange: boolean
): UserMatrixInterface => {
  const matrixForProjectSource = getProjectSource(projectSource);
  if (!matrixForProjectSource) return {};
  const matrixForRole = matrixForProjectSource[role];
  return {
    ...matrixForRole,
    isChangeEnabled: withRoleChange && matrixForRole.isChangeEnabled,
  };
};

export const getSaveButtonVisible = (
  projectSource: string,
  withRoleChange: boolean,
  fieldConfigs: { role: string }[]
): boolean => {
  const matrixForProjectSource = getProjectSource(projectSource);
  if (!matrixForProjectSource) return false;
  const listOfRolesOnPage = fieldConfigs.map(({ role }) => role);
  const rolesWithEnabledAction = Object.keys(matrixForProjectSource).filter(
    (role) => {
      if (listOfRolesOnPage.indexOf(role) === -1) return false;
      const matrixForRole = matrixForProjectSource[role];
      return (
        (withRoleChange && matrixForRole.isChangeEnabled) ||
        matrixForRole.isCreateEnabled ||
        matrixForRole.isDeleteEnabled
      );
    }
  );
  return rolesWithEnabledAction.length > 0;
};
